/**
 * Presentational settings for NUMBER fields (`custom_settings.number_format`).
 * The stored value is the number the user entered: `"12.23"` with `PERCENT`
 * renders as `12.23%`. There is no ratio conversion. See EKP-62394 /
 * unified-fields `NumberFieldCustomSettings`.
 */

export type NumberFormat = 'PLAIN' | 'PERCENT';

export type NumberDisplaySettings = {
  numberFormat: NumberFormat;
};

export const DEFAULT_NUMBER_DISPLAY: NumberDisplaySettings = {
  numberFormat: 'PLAIN',
};

export const parseNumberDisplaySettings = (
  customSettings: Record<string, unknown> | null | undefined,
): NumberDisplaySettings => {
  const rawFormat = customSettings?.number_format;
  const numberFormat: NumberFormat =
    rawFormat === 'PERCENT' ? 'PERCENT' : 'PLAIN';

  return { numberFormat };
};

export const toNumberDisplaySettingsAttribute = (
  settings: NumberDisplaySettings,
): Record<string, unknown> => ({
  number_format: settings.numberFormat,
});

/** Reads NUMBER display settings from Add/Edit Field form values. */
export const numberDisplayFromFormValues = (values: {
  number_format?: unknown;
}): NumberDisplaySettings =>
  parseNumberDisplaySettings({
    number_format: values.number_format,
  });

const parseFiniteNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Locale-format a stored NUMBER value. `PERCENT` appends `%` to the stored
 * number (`12.23` renders as `12.23%`).
 */
export const formatStoredNumber = (
  value: unknown,
  settings: NumberDisplaySettings = DEFAULT_NUMBER_DISPLAY,
): string | null => {
  const n = parseFiniteNumber(value);
  if (n == null) return null;

  const formatted = n.toLocaleString();
  return settings.numberFormat === 'PERCENT' ? `${formatted}%` : formatted;
};
